/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Grid, Typography } from '@mui/material';
import React from 'react';
import type { InfoDialogGeographicalCoverageProps } from '../../store/types';
import {
  findLargestMultiPolygonFirstChild,
  findLargestMultiPolygonLastChild,
  findLargestPolygonFirstChild,
  findLargestPolygonLastChild,
  findSmallestMultiPolygonFirstChild,
  findSmallestMultiPolygonLastChild,
  findSmallestPolygonFirstChild,
  findSmallestPolygonLastChild,
} from '../../utils/data-explorerUtils';
import { useDataExplorerTranslation } from '../../utils/i18n';

const InfoDialogGeographicalCoverage: React.FC<
  InfoDialogGeographicalCoverageProps
> = ({ coordinates, type }: InfoDialogGeographicalCoverageProps) => {
  const { t } = useDataExplorerTranslation();
  // Get the coordinates from geometry
  const isMultiPolygon = type === 'multipolygon' || type === 'MultiPolygon';
  const polygon = coordinates as number[][][];
  const multiPolygons = coordinates as number[][][][];

  return (
    <Grid
      container
      data-testid="infoDialogGeographicalCoverage"
      spacing={2}
      sx={{ flexDirection: 'column' }}
    >
      <Grid container sx={{ flexDirection: 'column' }} spacing={0}>
        <Grid>
          <Typography color="textSecondary" variant="caption">
            {t('geographical-coverage-westernmost')}
          </Typography>
        </Grid>
        <Grid>
          <Typography data-testid="infoDialogGeographicalCoverageWesternmost">
            {isMultiPolygon
              ? findSmallestMultiPolygonFirstChild(multiPolygons)
              : findSmallestPolygonFirstChild(polygon)}
            °
          </Typography>
        </Grid>
      </Grid>
      <Grid container sx={{ flexDirection: 'column' }} spacing={0}>
        <Grid>
          <Typography color="textSecondary" variant="caption">
            {t('geographical-coverage-easternmost')}
          </Typography>
        </Grid>
        <Grid>
          <Typography data-testid="infoDialogGeographicalCoverageEasternmost">
            {isMultiPolygon
              ? findLargestMultiPolygonFirstChild(multiPolygons)
              : findLargestPolygonFirstChild(polygon)}
            °
          </Typography>
        </Grid>
      </Grid>
      <Grid container sx={{ flexDirection: 'column' }} spacing={0}>
        <Grid>
          <Typography color="textSecondary" variant="caption">
            {t('geographical-coverage-northernmost')}
          </Typography>
        </Grid>
        <Grid>
          <Typography data-testid="infoDialogGeographicalCoverageNorthernmost">
            {isMultiPolygon
              ? findLargestMultiPolygonLastChild(multiPolygons)
              : findLargestPolygonLastChild(polygon)}
            °
          </Typography>
        </Grid>
      </Grid>
      <Grid container sx={{ flexDirection: 'column' }} spacing={0}>
        <Grid>
          <Typography color="textSecondary" variant="caption">
            {t('geographical-coverage-southernmost')}
          </Typography>
        </Grid>
        <Grid>
          <Typography data-testid="infoDialogGeographicalCoverageSouthernmost">
            {isMultiPolygon
              ? findSmallestMultiPolygonLastChild(multiPolygons)
              : findSmallestPolygonLastChild(polygon)}
            °
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default InfoDialogGeographicalCoverage;
